import { Link } from "react-router-dom";
import { Head } from "@/components/Head";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { Eyebrow, Section, SectionHeading } from "@/components/Section";
import { HeroItem, HeroStagger, Reveal } from "@/components/Reveal";
import { modules, pillars } from "@/data/modules";
import { verticalList } from "@/data/verticals";

const trustBadges: { name: string; href?: string }[] = [
  ...verticalList.map((vertical) => ({
    name: vertical.name,
    href: `/verticals/${vertical.slug}`,
  })),
  { name: "Appliance" },
];

const trustStats = [
  {
    value: "4+",
    label:
      "Trades running on one platform — HVAC, plumbing, electrical, roofing",
  },
  {
    value: "24/7",
    label: "AI voice coverage, so the phone never goes to voicemail",
  },
];

const pillarDetails: Record<
  string,
  { bullets: string[]; image?: { src: string; alt: string } }
> = {
  ServiTrex: {
    bullets: [
      "Multi-brand tenant control with role-based access for CSRs, dispatchers, managers, and finance",
      "Live dispatch board that balances skills, zones, and technician capacity",
      "Pricebook-driven estimates, invoices, and memberships attached to the customer record",
      "Accounting, inventory, and marketing automation layered on as the business grows",
    ],
  },
  TrexField: {
    bullets: [
      "Full job history, equipment records, and property notes on every device",
      "Photos, signatures, parts used, and payment capture right at the door",
      "Guided pricing so technicians quote the same way the office would",
      "Closeout syncs to the office instantly — nothing re-typed from a paper ticket",
    ],
    image: {
      src: "/img/mktg-field-tech.jpg",
      alt: "Field technician reviewing a job in the TrexField mobile app on a customer's driveway",
    },
  },
  "AI agents that answer": {
    bullets: [
      "Answers every inbound call, 24/7, trained on your company's voice and offers",
      "Books, reschedules, and qualifies leads straight onto the dispatch board",
      "Warm-transfers complex calls to a live CSR with full context already attached",
      "Usage-based minute bundles that scale with call volume — no seat tax on the phone",
    ],
  },
};

const outcomeStats = [
  { value: "<15 sec", label: "Average time to answer an inbound call" },
  { value: "100%", label: "Call answer rate with AI voice plus human backup" },
  {
    value: "1",
    label: "Shared system of record across office, field, and phones",
  },
  { value: "0", label: "Jobs re-keyed between the truck and the office" },
];

const testimonials = [
  {
    quote:
      "Before ServiTrex we lost calls every night after 6pm. Now the AI agent books the diagnostic, and my dispatcher sees it on the board before I've finished my coffee.",
    name: "Marcus Ferreira",
    role: "Owner, Ferreira Comfort Systems",
    trade: "HVAC",
  },
  {
    quote:
      "TrexField and the office board finally agree. My techs close out with photos and payment on-site, and invoicing doesn't wait three days for someone to retype a paper ticket.",
    name: "Dana Whitfield",
    role: "Operations Director, Whitfield Plumbing & Drain",
    trade: "Plumbing",
  },
];

export function HomePage() {
  return (
    <>
      <Head
        title="ServiTrex — Run your entire home-service business on one platform"
        description="ServiTrex unifies office operations, TrexField for technicians, and AI voice agents that answer every call for HVAC, plumbing, electrical, and roofing companies. Start a free trial or book a demo."
      />

      {/* 1. HERO */}
      <section className="relative overflow-hidden pb-10 pt-12 sm:pb-16 sm:pt-16 lg:pt-20">
        <div className="container-page">
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-10">
            <HeroStagger>
              <HeroItem>
                <Eyebrow>Office · Field · Phones — one platform</Eyebrow>
              </HeroItem>
              <HeroItem>
                <h1 className="max-w-2xl text-balance text-4xl text-fg sm:text-5xl lg:text-[3.4rem] lg:leading-[1.03]">
                  Run your entire home-service business on one platform — and
                  never miss a call again.
                </h1>
              </HeroItem>
              <HeroItem>
                <p className="mt-5 max-w-xl text-pretty text-lg text-muted sm:text-xl">
                  ServiTrex unifies dispatch, CRM, invoicing, and field
                  execution for HVAC, plumbing, electrical, and roofing
                  companies. AI voice agents answer, qualify, and book jobs
                  around the clock, so every lead lands on the board instead of
                  in voicemail.
                </p>
              </HeroItem>
              <HeroItem>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button to="/signup" size="lg">
                    Start free trial
                  </Button>
                  <Button to="/demo" variant="secondary" size="lg">
                    Book a demo
                  </Button>
                </div>
              </HeroItem>
              <HeroItem>
                <dl className="mt-10 grid max-w-xl grid-cols-3 gap-4 border-t border-border pt-6">
                  {[
                    { label: "Office product", value: "ServiTrex" },
                    { label: "Field app", value: "TrexField" },
                    { label: "Phone layer", value: "AI voice" },
                  ].map((item) => (
                    <div key={item.label}>
                      <dt className="text-xs uppercase tracking-[0.14em] text-muted">
                        {item.label}
                      </dt>
                      <dd className="mt-1 font-display text-lg font-semibold tracking-tight">
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </HeroItem>
            </HeroStagger>

            <HeroStagger>
              <HeroItem>
                <div className="relative mx-auto max-w-lg lg:ml-auto lg:mr-0 lg:max-w-none">
                  <div
                    aria-hidden
                    className="absolute -right-8 -top-10 hidden h-40 w-40 rounded-full bg-teal/20 blur-3xl sm:block"
                  />
                  <div
                    aria-hidden
                    className="absolute -bottom-10 -left-6 hidden h-32 w-32 rounded-full bg-accent-warm/15 blur-3xl sm:block"
                  />
                  <div className="relative overflow-hidden rounded-[1.75rem] border border-border bg-ink shadow-glow">
                    <div className="flex items-center gap-1.5 border-b border-white/10 bg-ink-soft px-4 py-3">
                      <span
                        className="h-2.5 w-2.5 rounded-full bg-danger/70"
                        aria-hidden
                      />
                      <span
                        className="h-2.5 w-2.5 rounded-full bg-accent-warm/70"
                        aria-hidden
                      />
                      <span
                        className="h-2.5 w-2.5 rounded-full bg-teal/70"
                        aria-hidden
                      />
                      <span className="ml-3 truncate text-xs text-fg-inverse/50">
                        app.servitrex.com/dispatch
                      </span>
                    </div>
                    <img
                      src="/img/mktg-hero-dashboard.jpg"
                      alt="ServiTrex dispatch dashboard showing the live job board, technician capacity, and an AI voice call being booked"
                      width={1200}
                      height={896}
                      className="block aspect-[4/3] w-full object-cover"
                      decoding="async"
                      fetchPriority="high"
                    />
                  </div>
                </div>
              </HeroItem>
            </HeroStagger>
          </div>
        </div>
      </section>

      {/* 2. TRUST STRIP */}
      <section className="border-y border-border bg-surface-muted/70 py-8 sm:py-10">
        <div className="container-page">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-muted">
            Built for the trades
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            {trustBadges.map((badge) =>
              badge.href ? (
                <Link
                  key={badge.name}
                  to={badge.href}
                  className="rounded-full border border-border bg-surface-raised px-4 py-2 font-display text-sm font-semibold tracking-tight text-fg transition hover:border-teal/50 hover:text-teal-deep"
                >
                  {badge.name}
                </Link>
              ) : (
                <span
                  key={badge.name}
                  className="rounded-full border border-dashed border-border bg-surface-raised/70 px-4 py-2 font-display text-sm font-semibold tracking-tight text-muted"
                >
                  {badge.name}
                </span>
              ),
            )}
          </div>
          <div className="mt-7 grid gap-3 sm:grid-cols-2">
            {trustStats.map((stat) => (
              <div
                key={stat.label}
                className="flex items-baseline gap-3 rounded-2xl border border-border bg-surface-raised/70 px-5 py-4"
              >
                <span className="font-display text-2xl font-bold tracking-tight text-teal-deep">
                  {stat.value}
                </span>
                <span className="text-sm text-muted">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. PRODUCT PILLARS */}
      <Section>
        <SectionHeading
          eyebrow="Three pillars, one system"
          title="Office. Field. Voice. Built to work as a single platform."
          description="Stop stitching together a phone vendor, a field app, and an office database that never quite agree. ServiTrex, TrexField, and AI voice share one record from first ring to final invoice."
        />
        <div className="mt-12 flex flex-col gap-10">
          {pillars.map((pillar, index) => {
            const detail = pillarDetails[pillar.title];
            const reversed = index % 2 === 1;
            return (
              <Reveal key={pillar.title} delay={index * 0.06}>
                <article
                  className={`grid items-center gap-8 rounded-[1.75rem] border border-border bg-surface-raised p-6 shadow-soft sm:p-8 lg:grid-cols-2 lg:gap-12 ${
                    reversed ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
                      {pillar.eyebrow}
                    </p>
                    <h3 className="mt-3 font-display text-3xl tracking-tight">
                      {pillar.title}
                    </h3>
                    <p className="mt-3 text-pretty text-muted">{pillar.body}</p>
                    <ul className="mt-5 space-y-2.5">
                      {detail?.bullets.map((bullet) => (
                        <li key={bullet} className="flex gap-3 text-sm text-fg">
                          <span
                            aria-hidden
                            className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal"
                          />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    {(pillar.href === "/product/office" ||
                      pillar.href === "/product/field") && (
                      <Link
                        to={pillar.href}
                        className="mt-6 inline-flex text-sm font-semibold text-teal-deep hover:text-fg"
                      >
                        {pillar.cta} →
                      </Link>
                    )}
                    {pillar.href === "/demo" && (
                      <Link
                        to="/demo"
                        className="mt-6 inline-flex text-sm font-semibold text-teal-deep hover:text-fg"
                      >
                        {pillar.cta} →
                      </Link>
                    )}
                  </div>
                  <div>
                    {detail?.image ? (
                      <img
                        src={detail.image.src}
                        alt={detail.image.alt}
                        width={1376}
                        height={768}
                        loading="lazy"
                        decoding="async"
                        className="aspect-[16/10] w-full rounded-2xl border border-border object-cover shadow-soft"
                      />
                    ) : (
                      <div className="rounded-2xl border border-border bg-ink p-6 text-fg-inverse shadow-soft">
                        <p className="text-xs uppercase tracking-[0.16em] text-teal">
                          {pillar.title === "ServiTrex"
                            ? "Dispatch board"
                            : "Live call intake"}
                        </p>
                        <p className="mt-3 font-display text-xl">
                          {pillar.title === "ServiTrex"
                            ? "Capacity, skills, and brand context in one view."
                            : "“I can get a tech to 1842 Harbor Ave this afternoon.”"}
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2">
                          {(pillar.title === "ServiTrex"
                            ? ["Board", "CRM", "Invoicing"]
                            : ["Booked", "CRM updated", "Ready to dispatch"]
                          ).map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-fg-inverse"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* 4. AI VOICE SPOTLIGHT */}
      <Section className="bg-surface-muted/50">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div>
              <Eyebrow>AI voice agents</Eyebrow>
              <h2 className="text-balance text-3xl text-fg sm:text-4xl lg:text-[2.6rem]">
                The phone stops being the bottleneck.
              </h2>
              <p className="mt-4 max-w-xl text-pretty text-base text-muted sm:text-lg">
                Every unanswered call is a job that books with a competitor
                instead. ServiTrex's AI voice agents pick up on the first ring,
                day or night, and hand dispatch a clean, structured job — not a
                voicemail to chase down in the morning.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  {
                    title: "Never miss a call",
                    body: "AI answers every inbound line, even during storms, holidays, and after hours, in your company's voice.",
                  },
                  {
                    title: "24/7 booking, not just triage",
                    body: "Jobs land on the dispatch board scheduled and structured — no callback queue, no manual re-entry.",
                  },
                  {
                    title: "Warm handoff when it matters",
                    body: "Complex or high-value calls transfer to a live CSR with full context already attached to the record.",
                  },
                  {
                    title: "Metered, usage-based minutes",
                    body: "Buy a minute bundle sized to your call volume and scale up as booked work grows — no per-seat phone tax.",
                  },
                ].map((item) => (
                  <li
                    key={item.title}
                    className="rounded-2xl border border-border bg-surface-raised p-4"
                  >
                    <p className="font-display text-base font-semibold tracking-tight">
                      {item.title}
                    </p>
                    <p className="mt-1 text-sm text-muted">{item.body}</p>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button to="/demo" size="lg">
                  Book a demo
                </Button>
                <Button to="/pricing" variant="ghost" size="lg">
                  See voice minute bundles
                </Button>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div
                aria-hidden
                className="absolute -left-10 -bottom-10 hidden h-40 w-40 rounded-full bg-teal/20 blur-3xl lg:block"
              />
              <img
                src="/img/mktg-ai-voice.jpg"
                alt="Visualization of an AI voice agent answering a call and booking a service appointment"
                width={1200}
                height={896}
                loading="lazy"
                decoding="async"
                className="relative aspect-[4/3] w-full rounded-[1.75rem] border border-border object-cover shadow-glow"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      {/* 5. MODULE GRID */}
      <Section>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Modules"
            title="Everything the business touches after the phone rings"
            description="Start with core operations. Layer accounting, inventory, workforce, and AI voice usage as the company grows."
          />
          <Button to="/pricing" variant="secondary">
            See what each plan includes
          </Button>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {modules.map((module, index) => (
            <Reveal key={module.title} delay={(index % 3) * 0.05}>
              <article className="h-full rounded-3xl border border-border bg-surface-raised p-5">
                <div className="mb-4 h-1.5 w-12 rounded-full bg-teal" />
                <h3 className="font-display text-xl tracking-tight">
                  {module.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">
                  {module.body}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 6. VERTICALS SHOWCASE */}
      <Section className="bg-surface-muted/50">
        <SectionHeading
          eyebrow="Verticals"
          title="Purpose-built for the trades you already run"
          description="Same platform, trade-aware workflows for HVAC, plumbing, electrical, and roofing — with language and job flows your CSRs and techs already recognize."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {verticalList.map((vertical, index) => (
            <Reveal key={vertical.slug} delay={index * 0.05}>
              <Link
                to={`/verticals/${vertical.slug}`}
                className="group grid min-h-40 grid-cols-[1fr_auto] items-end gap-4 rounded-[1.75rem] border border-border bg-surface-raised p-6 transition hover:border-teal/40 hover:shadow-glow"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
                    {vertical.name}
                  </p>
                  <h3 className="mt-2 max-w-md font-display text-2xl tracking-tight">
                    {vertical.headline}
                  </h3>
                </div>
                <span className="text-sm font-semibold text-muted transition group-hover:text-teal-deep">
                  Explore →
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 7. OUTCOMES / STATS BAND */}
      <section className="py-16 sm:py-20">
        <div className="container-page">
          <Reveal>
            <div className="rounded-[2rem] border border-border bg-ink px-6 py-10 text-fg-inverse shadow-soft sm:px-10 sm:py-12">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal">
                What changes when it's one system
              </p>
              <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                {outcomeStats.map((stat) => (
                  <div key={stat.label}>
                    <p className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                      {stat.value}
                    </p>
                    <p className="mt-2 text-sm text-fg-inverse/70">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      <Section>
        <SectionHeading
          eyebrow="Owners running on ServiTrex"
          title="Built for people who answer the phone for a living"
        />
        <div className="mt-10 grid gap-5 lg:grid-cols-2">
          {testimonials.map((testimonial, index) => (
            <Reveal key={testimonial.name} delay={index * 0.06}>
              <figure className="flex h-full flex-col justify-between rounded-[1.75rem] border border-border bg-surface-raised p-7 shadow-soft">
                <blockquote>
                  <p className="text-pretty font-display text-xl leading-snug tracking-tight text-fg sm:text-2xl">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                </blockquote>
                <figcaption className="mt-6 flex items-center justify-between gap-4 border-t border-border pt-4">
                  <div>
                    <p className="font-semibold text-fg">{testimonial.name}</p>
                    <p className="text-sm text-muted">{testimonial.role}</p>
                  </div>
                  <span className="rounded-full bg-surface-muted px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-teal-deep">
                    {testimonial.trade}
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 9. FINAL CTA */}
      <CtaBand />
    </>
  );
}
